import * as THREE from 'three';
import {
  FOCUS_CAM_LERP,
  FOCUS_SNAP_EPS,
  FOCUS_TARGET_LERP,
} from '../constants';
import { bindScenePointer } from './bindScenePointer';
import { bindSceneResize } from './bindSceneResize';
import { createCameraFraming } from './createCameraFraming';
import { createIgnitionEffects } from './createIgnitionEffects';
import { createNeuralGraphLayer } from './createNeuralGraphLayer';
import { createSceneShell } from './createSceneShell';
import { createVoidDust } from './neuralMaterials';
import { runNeuralAnimationFrame } from './runNeuralAnimationFrame';
import { NEURAL_FRAME_MS } from './neuralFrameBudget';

let sceneGeneration = 0;

export function setupNeuralScene({
  container,
  graphRef,
  floatingCardWrapRef,
  focusMissionRef,
  neuralSceneApiRef,
  setSelectedMissionIdRef,
  selectedMissionIdRef,
  renameTagRef,
}) {
  const generation = ++sceneGeneration;
  const shell = createSceneShell(container);
  const {
    canvas,
    hoverNameEl,
    hoverNameText,
    labelRenderer,
    scene,
    camera,
    renderer,
    composer,
    bloom,
    output,
    dither,
    controls,
    motionQuery,
    nodeGroup,
    viewDir,
    INITIAL_DIST,
    MIN_DIST,
    MAX_DIST,
    ZOOM_DAMPING,
    ZOOM_IMPULSE,
    ZOOM_FRICTION,
  } = shell;

  const motion = { reduced: motionQuery.matches };
  const driftRef = { fresh: true, t: 0, yaw: 0, pitch: 0 };
  const interactionRef = { dragging: false, pointerHeld: false, lastAt: 0, pointerAt: 0 };
  const targetDistRef = { v: INITIAL_DIST };
  const zoomCursorRef = { x: 0, y: 0, live: false, hasPivot: false, px: 0, py: 0, pz: 0 };
  const zoomDriveRef = { vel: 0 };
  const focusTargetGoalRef = { v: null };
  const focusCameraGoalRef = { v: null };
  const pulseTRef = { v: 0 };
  const hostRect = { left: 0, top: 0, width: 1, height: 1 };
  const pauseIdle = ({ syncDist = true } = {}) => {
    driftRef.fresh = true;
    interactionRef.lastAt = performance.now();
    if (syncDist) targetDistRef.v = camera.position.distanceTo(controls.target);
    zoomDriveRef.vel = 0;
  };

  const graph = createNeuralGraphLayer({ nodeGroup, graphRef, renameTagRef });
  const ignition = createIgnitionEffects(graph);
  const voidDust = createVoidDust();
  scene.add(voidDust);

  const { meshByTaskId, syncSelection, refreshPresentation } = graph;

  const onMotionChange = (e) => {
    motion.reduced = e.matches;
    if (motion.reduced) {
      driftRef.fresh = true;
      focusTargetGoalRef.v = null;
      focusCameraGoalRef.v = null;
    }
  };
  motionQuery.addEventListener('change', onMotionChange);

  const { frameOverview, frameIgnition, focusMission, syncOverviewDistance } = createCameraFraming({
    camera,
    controls,
    container,
    graphRef,
    meshByTaskId,
    labelByTaskId: graph.labelByTaskId,
    nebula: graph.nebula,
    viewDir,
    MIN_DIST,
    MAX_DIST,
    INITIAL_DIST,
    targetDistRef,
    focusTargetGoalRef,
    focusCameraGoalRef,
    driftRef,
    motion,
    pauseIdle,
  });

  const raycasterCtx = {
    hoverPointerId: null,
    lastPointer: { x: null, y: null },
    vectors: {
      worldPos: new THREE.Vector3(),
      vecToCam: new THREE.Vector3(),
      normalOut: new THREE.Vector3(),
      camDir: new THREE.Vector3(),
      toPointFromCam: new THREE.Vector3(),
      projectForCard: new THREE.Vector3(),
      tmpVb: new THREE.Vector3(),
      tmpVa: new THREE.Vector3(),
    },
  };

  focusMissionRef.current = focusMission;

  const { applyHoverRaycast, disposePointer } = bindScenePointer({
    container,
    canvas,
    camera,
    controls,
    meshByTaskId,
    nodeGroup,
    raycasterCtx,
    interactionRef,
    hostRect,
    setSelectedMissionIdRef,
    syncSelection,
    pauseIdle,
    focusTargetGoalRef,
    focusCameraGoalRef,
    targetDistRef,
    MIN_DIST,
    MAX_DIST,
    zoomCursorRef,
    zoomDriveRef,
    ZOOM_IMPULSE,
  });

  const autoOverview = { pending: true, settle: 0 };
  const requestOverview = () => {
    autoOverview.pending = true;
    autoOverview.settle = 0;
  };
  const flushAutoOverview = () => {
    if (!autoOverview.pending && autoOverview.settle <= 0) return;
    if (container.clientWidth < 32 || container.clientHeight < 32) return;
    if (selectedMissionIdRef.current) {
      autoOverview.pending = false;
      autoOverview.settle = 0;
      return;
    }
    frameOverview(null, { snap: true });
    if (autoOverview.pending) {
      autoOverview.pending = false;
      autoOverview.settle = 5;
    } else {
      autoOverview.settle -= 1;
    }
  };

  neuralSceneApiRef.current = {
    syncSelection,
    refreshPresentation,
    frameOverview,
    requestOverview,
    ignite(event) {
      ignition.fire(event, performance.now(), motion.reduced);
      autoOverview.pending = false;
      autoOverview.settle = 0;
      frameIgnition(event);
    },
    clearIgnition() { ignition.clear(); },
  };

  const { disposeResize } = bindSceneResize({
    container,
    camera,
    renderer,
    composer,
    bloom,
    labelRenderer,
    graph,
    voidDust,
    hostRect,
    selectedMissionIdRef,
    frameOverview,
    requestOverview,
    syncOverviewDistance,
  });

  let raf = 0;
  let stopped = false;
  let previousTime = performance.now();
  const tick = (now) => {
    if (stopped || generation !== sceneGeneration || !canvas.isConnected) return;
    raf = requestAnimationFrame(tick);
    if (document.hidden) return;
    if (now - previousTime < NEURAL_FRAME_MS) return;

    const delta = Math.min((now - previousTime) / 1000, 0.05);
    previousTime = now;
    if (!motion.reduced && !interactionRef.dragging) voidDust.rotation.z += delta * 0.004;
    if (!interactionRef.dragging && raycasterCtx.lastPointer.x != null) {
      applyHoverRaycast(raycasterCtx.lastPointer.x, raycasterCtx.lastPointer.y);
    }
    runNeuralAnimationFrame({
      graph,
      camera,
      controls,
      renderer,
      composer,
      ignition,
      now,
      delta,
      reducedMotion: motion.reduced,
      labelRenderer,
      scene,
      container,
      floatingCardWrapRef,
      hoverNameEl,
      hoverNameText,
      raycaster: raycasterCtx,
      INITIAL_DIST,
      MIN_DIST,
      MAX_DIST,
      ZOOM_DAMPING,
      targetDistRef,
      focusTargetGoalRef,
      focusCameraGoalRef,
      pulseTRef,
      driftRef,
      interactionRef,
      FOCUS_TARGET_LERP,
      FOCUS_CAM_LERP,
      FOCUS_SNAP_EPS,
      zoomCursorRef,
      zoomDriveRef,
      MIN_DIST,
      MAX_DIST,
      ZOOM_FRICTION,
    });
    flushAutoOverview();
  };
  raf = requestAnimationFrame(tick);

  syncSelection(selectedMissionIdRef.current);

  return () => {
    stopped = true;
    focusMissionRef.current = null;
    neuralSceneApiRef.current = null;
    cancelAnimationFrame(raf);
    disposePointer();
    disposeResize();
    controls.dispose();
    motionQuery.removeEventListener('change', onMotionChange);
    ignition.clear();
    scene.remove(voidDust);
    voidDust.geometry.dispose();
    voidDust.material.dispose();
    bloom.dispose();
    output.dispose();
    dither.dispose();
    composer.dispose();
    graph.glowTexture.dispose();
    graph.disposeNodeInstances?.();

    if (canvas.parentNode === container) container.removeChild(canvas);
    if (labelRenderer.domElement.parentNode === container) {
      container.removeChild(labelRenderer.domElement);
    }
    hoverNameEl.remove();

    graph.meshByTaskId.clear();
    graph.labelByTaskId.clear();
    graph.edgeLinesByKey.clear();
    graph.haloByTaskId.clear();

    scene.fog = null;
    nodeGroup.traverse((obj) => {
      if (obj.geometry && !obj.userData.sharedGeom) obj.geometry.dispose();
      if (obj.material) {
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach((m) => m.dispose?.());
      }
    });
    renderer.dispose();
  };
}
